import axios from 'axios';

const runtimeOrigin =
  typeof window !== 'undefined' ? window.location.origin : '';
const rawApiUrl = process.env.REACT_APP_API_URL || runtimeOrigin;

export const API_URL = rawApiUrl.replace(/\/+$/, '');


export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

export default api;

// Every request: inject active workspace header
api.interceptors.request.use((config) => {
  // Inject the active workspace header for workspace-scoped routes
  const activeWorkspaceRaw = localStorage.getItem('activeWorkspace');
  if (activeWorkspaceRaw) {
    try {
      const activeWorkspace = JSON.parse(activeWorkspaceRaw);
      if (activeWorkspace?._id) {
        config.headers['x-workspace-id'] = activeWorkspace._id;
      }
    } catch (_) { /* malformed, skip */ }
  }

  return config;
});

// Global Error Handler for AppError standardization and 401 redirection
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorCode = error.response?.data?.error || error.response?.data?.code;

    // 1. Handle Session Expiry / Unauthorized (401)
    if (status === 401 && !error.config?.silence401) {
      // Clear workspace context on session failure to prevent stale UI state
      localStorage.removeItem('activeWorkspace');
      
      // Force reload to login if we are not already there
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?expired=true';
      }
    }

    // 2. Handle Stale Workspace (404 or specific AppError code)
    // If backend returns 404 for a workspace-scoped route, it usually means the x-workspace-id is invalid.
    if ((status === 404 || status === 403) && (errorCode === 'ERR_NOT_FOUND' || errorCode === 'ERR_FORBIDDEN')) {
      const message = error.response?.data?.message || '';
      if (message.toLowerCase().includes('workspace')) {
        console.warn('Stale or invalid workspace detected. Clearing local context.');
        localStorage.removeItem('activeWorkspace');
        
        // If we are deep in the app, redirect to dashboard which usually triggers a refresh/re-fetch of workspaces
        if (window.location.pathname !== '/' && window.location.pathname !== '/dashboard') {
           window.location.href = '/dashboard?reset_workspace=true';
        }
      }
    }

    // 3. Standardize backend AppError objects into a predictable format for frontend UI
    if (error.response?.data?.error === 'AppError' || errorCode) {
      const appErr = {
        isAppError: true,
        code: errorCode || 'UNKNOWN_ERROR',
        message: error.response?.data?.message || 'An unexpected error occurred.',
        status: status
      };
      return Promise.reject(appErr);
    }

    return Promise.reject(error);
  }
);

export const login = (email, password, role) =>
  role === 'user'
    ? api.post('/user/login', { email, password })
    : api.post('/admin/login', { email, password });

export const register = (name, email, password) =>
  api.post('/user/register', { name, email, password });

export const resetPassword = (email, password) =>
  api.post('/user/reset-password', { email, password });

export const getQueueEmails = (page = 1, limit = 20, filters = {}) => {
  const params = new URLSearchParams({ page, limit });
  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      params.append(key, value);
    }
  });
  return api.get(`/api/insights/queue-emails?${params.toString()}`);
};

export const getInbox = (page = 1, limit = 20) =>
  api.get(`/api/mail-accounts/inbox?page=${page}&limit=${limit}`);

export const fetchInboxApi = () =>
  api.post('/api/mail-accounts/fetch-inbox');

export const getProcessingEmails = (page = 1, limit = 20, filters = {}) => {
  const params = new URLSearchParams({ page, limit, status: 'processing' });
  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      params.append(key, value);
    }
  });
  return api.get(`/api/insights/queue-emails?${params.toString()}`);
};

export const getNotSendedEmails = (page = 1, limit = 20, filters = {}) => {
  const params = new URLSearchParams({ page, limit, status: 'error' });
  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      params.append(key, value);
    }
  });
  return api.get(`/api/insights/queue-emails?${params.toString()}`);
};

export const getEmailStatsByDate = (params = {}) => { // params: { days, startDate, endDate }
  const query = new URLSearchParams(params).toString();
  return api.get(`/api/insights/stats-by-date?${query}`);
};

export const getEmailStatsByCountry = () =>
  api.get('/api/insights/stats-by-country');

export const uploadCsv = (file, offset = 0) => {
  const formData = new FormData();
  formData.append('file', file);
  if (offset) formData.append('offset', offset.toString());
  return api.post('/queue/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const detectContactsImportFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/api/contacts/import/intake', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const detectPastedContactsImport = (text) =>
  api.post('/api/contacts/import/paste-intake', { text });

export const detectSourceContactsImport = (personIds) =>
  api.post('/api/contacts/import/source-intake', { personIds });

export const previewContactsImport = (intakePayload, mappingPayload) =>
  api.post('/api/contacts/import/preview', { intakePayload, mappingPayload });

export const executeContactsImport = (intakePayload, mappingPayload) =>
  api.post('/api/contacts/import/execute', { intakePayload, mappingPayload });

export const getContactsImportHistory = () =>
  api.get('/api/contacts/import/history');

export const getContactsImportHistoryDetail = (id) =>
  api.get(`/api/contacts/import/history/${id}`);

export const searchSourceImportPeople = (payload) =>
  api.post('/api/global-pool/people/search', payload);

export const sendBatch = () =>
  api.post('/queue/send-batch');

export const getRecentEmails = () =>
  api.get('/api/insights/recent-activity');

export const getDashboardSummary = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return api.get(`/api/insights/dashboard-summary${query ? `?${query}` : ''}`);
};

export const getTopCompanies = () =>
  api.get('/api/insights/companies-summary');

export const getCompanies = () =>
  api.get('/api/insights/companies');

export const sendQueueEmail = (id) =>
  api.post(`/queue/send/${id}`);

export const removeQueueEmail = (id) =>
  api.delete(`/queue/${id}`);

export const exportQueueEmails = () =>
  api.get('/queue/export', { responseType: 'blob' });

export const getMailAccounts = () => api.get('/api/mail-accounts');
export const addMailAccount = (data) => api.post('/api/mail-accounts', data);
export const updateMailAccount = (id, data) => api.put(`/api/mail-accounts/${id}`, data);
export const deleteMailAccount = (id) => api.delete(`/api/mail-accounts/${id}`);

export const getAllCompanyData = () => api.get('/api/companies');

// Email Finder
export const processApolloHtml = (html) => api.post('/email-finder/process-html', { html });
export const processAndVerify = (html) => api.post('/email-finder/process-and-verify', { html }, { timeout: 300000 });
export const verifySingleEmail = (email) => api.post('/email-finder/verify-single', { email });
export const checkDomain = (domain) => api.get(`/email-finder/check-domain/${domain}`);
export const getAllSavedEmails = (params) => api.get('/email-finder/all-saved-emails', { params });
export const importToWorkspace = (data) => api.post('/email-finder/import-to-workspace', data);
export const deleteSavedBatch = (batchId) => api.delete(`/email-finder/batch/${batchId}`);
export const deleteSavedContact = (id) => api.delete(`/email-finder/contact/${id}`);
export const retrySavedBatch = (batchId) => api.post(`/email-finder/retry-batch/${batchId}`);
export const retrySavedContact = (id) => api.post(`/email-finder/retry-contact/${id}`);

// Workspaces
export const createWorkspace = (name) => api.post('/api/workspace', { name });
export const getWorkspaces = () => api.get('/api/workspace');

// AI Template Generation
export const generateAITemplate = (prompt, variables = []) =>
  api.post('/api/templates/ai-generate', { prompt, variables });

export const generateAITemplateVariations = (prompt, variables = [], agentCount = 4) =>
  api.post('/api/templates/ai-generate-variations', { prompt, variables, agentCount });
